import { apiClient } from '../utils/api-client';
import { throwForResponse } from './api-error';
import type { Placement, ScoreCard } from './round-scores';
import type { RoundStatus, RoundType } from '../types/competition-round';

/**
 * A competition's standings per class, across the rounds each one climbs
 * (`Api::Competitions::StandingsController`).
 *
 * **A class's turns are one contest.** When a class is split over bookable
 * rounds — a morning and an afternoon — its athletes are ranked in one table
 * across them, each row saying which turn its cards come from. Its other rounds
 * (a final) are ranked on their own.
 *
 * **The server sends the places**, with the cards behind them: every row
 * carries its `rank` and `score` in the table it sits in (the merged turns, one
 * turn shown apart, or a round of its own), by the competition's regulation
 * (`RankingStrategies`). The client draws them and computes none.
 *
 * Live for whoever keeps the score here (admins, editors, judges); for anybody
 * else only once every turn of the class is closed (`turns_not_closed`).
 *
 * **Tie-breaks** (issue #364) are the organisers' (`manage`): a tie is called,
 * the athletes climb once more, and the order they finished in is typed in.
 * The rows say what it did (`tiebreak` on each place); only the organisers are
 * sent the tie-breaks themselves and the ties still open, on each group.
 */

/** A card, with the problem's number within the class — how turns are matched. */
export interface StandingCard extends ScoreCard {
  number: number;
}

/** One of the class's problems in a round: its number within the class. */
export interface StandingProblem {
  id: string;
  number: number;
  name: string | null;
  /** Lead: the holds on the route. Null on a boulder block. */
  holds_count: number | null;
  /** Whether a judge stands at it — under OPES, also what it is worth. */
  requires_judge?: boolean;
}

export interface StandingRound {
  id: string;
  name: string;
  round_type: RoundType;
  status: RoundStatus;
  closed: boolean;
  bookable: boolean;
  start_time: string | null;
  /** The class's problems here, numbered from 1. */
  problems: StandingProblem[];
}

/**
 * One athlete of the class: a name, a squad, the cards of one round, and the
 * place they earn in the table they sit in (null: not placed yet).
 */
export interface StandingEntry extends Placement {
  id: string;
  /** Name and surname — never an address. Null when the desk never typed one. */
  full_name: string | null;
  team_name: string | null;
  /** Climbed with an account of their own (never a child's managed one). */
  has_account?: boolean;
  /** That account's avatar, when it has one. */
  avatar_url?: string | null;
  /** That account's profile (`/users/:slug`). */
  user_slug?: string | null;
  /**
   * The round the cards come from: the turn booked, or — booked on none — the
   * first turn with results. Null for somebody who has climbed nowhere yet.
   */
  round_id: string | null;
  /** Other turns this (unbooked) athlete also has results on: a desk's mistake to put straight. */
  also_climbed_in: string[];
  /** A class split over two or more turns, and none booked. */
  turn_to_choose: boolean;
  cards: StandingCard[];
}

/**
 * Why a class's turns cannot be ranked as one table. `judged_blocks`: under a
 * regulation that tells judged blocks apart, the same block number is judged
 * in one turn and self-certified in another.
 */
export type IncomparableReason = 'round_types' | 'problem_counts' | 'judged_blocks';

/**
 * Rounds ranked together: the class's turns, or one round that is not a turn.
 * `comparable` is false only for turns that give the class a different number
 * of problems, are not the same discipline, or (under OPES) do not judge the
 * same blocks — then each is shown apart.
 */
/** One athlete of a tie-break, and where they finished in it once it is decided. */
export interface TiebreakEntry {
  participant_id: string;
  position: number | null;
}

/** A tie-break of a group, as the organisers are sent it. */
export interface Tiebreak {
  id: string;
  /** The place it was called for — its name ("Spareggio per il 1° posto"). */
  rank: number;
  /** 2 for the second one among the same athletes for the same place: "(2)". */
  sequence: number;
  /** The tie-break whose outcome left these athletes level. */
  parent_id: string | null;
  decided: boolean;
  /** Its athletes are no longer exactly a tie: it counts for nothing. */
  stale: boolean;
  /** A follow-up hangs off it, so it cannot be deleted first. */
  has_follow_ups: boolean;
  entries: TiebreakEntry[];
}

/** Athletes still level after every tie-break: nobody called (`tiebreak_id` null), or being called. */
export interface StandingTie {
  rank: number;
  participant_ids: string[];
  parent_id: string | null;
  tiebreak_id: string | null;
}

export interface StandingGroup {
  comparable: boolean;
  reason: IncomparableReason | null;
  /** The block numbers judged in some turns and not in others (`judged_blocks`). */
  judged_mismatch?: number[];
  /** The discipline whose ranking applies. Null when the turns mix them. */
  round_type: RoundType | null;
  rounds: StandingRound[];
  entries: StandingEntry[];
  /** The organisers' only: the group (null for the turns, else its round). */
  scope_round_id?: string | null;
  tiebreaks?: Tiebreak[];
  ties?: StandingTie[];
  /** Every round of the group under review or closed, and the stage not archived. */
  can_add_tiebreak?: boolean;
  /** The rounds a tie-break waits on. */
  rounds_not_ended?: { id: string; name: string }[];
}

export interface CategoryStandings {
  category: { id: string; name: string };
  /** The regulation the places come from (a key of `RankingStrategies`). */
  ranking_strategy?: string;
  /** Whether it tells judged blocks from self-certified ones. */
  ranking_judged_blocks?: boolean;
  /** The class's turns as one group; null when it has none. */
  turns: StandingGroup | null;
  /** Each of its other rounds, one group each (only the closed ones, unless live). */
  rounds: StandingGroup[];
  /** Whether this is the scorers' live view rather than the room's. */
  live: boolean;
  /** Whether the viewer calls and decides the tie-breaks (admins and editors). */
  manage?: boolean;
}

/** A class that climbs at least one round here: a tab, or a link. */
export interface StandingsCategory {
  id: string;
  name: string;
  /** How many of its rounds are turns (bookable). */
  turns_count: number;
  /** How many are not. */
  rounds_count: number;
  /** It has turns, and every one is closed: the room may read the merged table. */
  turns_closed: boolean;
}

export interface StandingsCategories {
  categories: StandingsCategory[];
  live: boolean;
}

interface ListResponse {
  data: StandingsCategory[];
  meta?: { live?: boolean };
}

interface ShowResponse {
  data: Omit<CategoryStandings, 'live' | 'manage'>;
  meta?: { live?: boolean; manage?: boolean };
}

class CategoryStandingsService {
  /** The classes that climb here, with their turns. */
  async list(competitionSlug: string): Promise<StandingsCategories> {
    const response = await apiClient.get(`/api/competitions/${competitionSlug}/standings`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the standings');
    }

    const data: ListResponse = await response.json();
    return { categories: data.data, live: !!data.meta?.live };
  }

  /** One class's standings across its rounds. */
  async get(competitionSlug: string, categoryId: string): Promise<CategoryStandings> {
    const response = await apiClient.get(
      `/api/competitions/${competitionSlug}/standings/${encodeURIComponent(categoryId)}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the standings');
    }

    return this.read(response);
  }

  /**
   * Calls a tie-break on a tie: `roundId` is the group's (null for the turns),
   * `participantIds` exactly the athletes level in it. Answers with the class's
   * standings, the tie-break in them.
   */
  async addTiebreak(
    competitionSlug: string,
    categoryId: string,
    roundId: string | null,
    participantIds: string[]
  ): Promise<CategoryStandings> {
    const response = await apiClient.post(this.tiebreaksPath(competitionSlug, categoryId), {
      data: { attributes: { round_id: roundId, participant_ids: participantIds } },
    });
    return this.read(response);
  }

  /** The outcome: a position per athlete, equal ones for those still level. */
  async decideTiebreak(
    competitionSlug: string,
    categoryId: string,
    tiebreakId: string,
    positions: Record<string, number>
  ): Promise<CategoryStandings> {
    const response = await apiClient.patch(this.tiebreaksPath(competitionSlug, categoryId, tiebreakId), {
      data: { attributes: { positions } },
    });
    return this.read(response);
  }

  async deleteTiebreak(competitionSlug: string, categoryId: string, tiebreakId: string): Promise<CategoryStandings> {
    const response = await apiClient.delete(this.tiebreaksPath(competitionSlug, categoryId, tiebreakId));
    return this.read(response);
  }

  private tiebreaksPath(competitionSlug: string, categoryId: string, tiebreakId?: string) {
    const base = `/api/competitions/${competitionSlug}/standings/${encodeURIComponent(categoryId)}/tiebreaks`;
    return tiebreakId ? `${base}/${encodeURIComponent(tiebreakId)}` : base;
  }

  private async read(response: Response): Promise<CategoryStandings> {
    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the standings');
    }

    const data: ShowResponse = await response.json();
    return { ...data.data, live: !!data.meta?.live, manage: !!data.meta?.manage };
  }
}

export const categoryStandingsService = new CategoryStandingsService();
